import {
  afterNextRender,
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  Injector,
  OnDestroy,
  OnInit,
  signal,
  TemplateRef,
  viewChild,
  WritableSignal,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { FakeSearchbarComponent } from '@myarvato/components/fake-searchbar';
import { ErrorMessageComponent, PrimeNGError } from '@myarvato/components/message';
import { getSearchWidgetConfig, SearchWidgetComponent } from '@myarvato/components/search-widget';
import { UserTabDto } from '@myarvato/core/customization';
import { ViewApplicationResult, ViewDataService, ViewDialogService, ViewFilterService, ViewManagerComponent } from '@myarvato/core/view';
import { CalendarComponent, DateRange } from '@myarvato/shared/calendar';
import {
  ColumnDefinitionBase,
  DataTableSearchDto,
  ManualLazyLoad,
  MyArvatoColumnManagerComponent,
  MyArvatoTreeTableComponent,
  TreeTableCoordinatorService,
  TreeTableFilterService,
} from '@myarvato/shared/table';
import { KpiBarComponent, KpiCardDefinition } from '@myarvato/components/kpi';
import { TitleComponent } from '@myarvato/shared/title';
import { NgbDropdownModule, NgbPopoverModule } from '@ng-bootstrap/ng-bootstrap';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import dayjs from 'dayjs';
import { MatomoTrackClickDirective } from 'ngx-matomo-client';
import { FilterMetadata, MenuItem, TreeNode } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { MenuModule } from 'primeng/menu';
import { CheckboxModule } from 'primeng/checkbox';
import { DialogModule } from 'primeng/dialog';
import { FloatLabelModule } from 'primeng/floatlabel';
import { InputTextModule } from 'primeng/inputtext';
import { ScrollPanelModule } from 'primeng/scrollpanel';
import { TreeTableColumnReorderEvent, TreeTableLazyLoadEvent } from 'primeng/treetable';
import { ManageKpiBarComponent } from './manage-kpi-bar/manage-kpi-bar.component';
import { ColumnProviderService } from './services/column-provider.service';
import { TrackingListExportService } from './services/tracking-list-export.service';
import { UserColumnService } from './services/user-column.service';
import { TransportDetailNavigationService } from '@myarvato/core/services';
import { getDialogResult, MyArvatoDialogService } from '@myarvato/shared/dialog';
import { FilterRecord, KpiStateDto, SearchDtoBase } from '@myarvato/shared/models';
import {
  OverviewDto,
  OverviewRecordDto,
  TRANSPORT_SEARCH_ALLOWED_MAX_LENGTH,
  TransportSearchIndexDto,
  TransportUserTabDataDto,
} from '@myarvato/modules/transport/models';
import { TransportDataService, TransportSearchService } from '@myarvato/modules/transport/services';
import { KpiService } from '@myarvato/modules/transport/kpis';

@Component({
  selector: 'transport-tracking-list',
  imports: [
    TitleComponent,
    MyArvatoTreeTableComponent,
    ManageKpiBarComponent,
    TranslatePipe,
    CalendarComponent,
    FormsModule,
    MatomoTrackClickDirective,
    KpiBarComponent,
    NgbDropdownModule,
    NgbPopoverModule,
    MyArvatoColumnManagerComponent,
    FakeSearchbarComponent,
    ViewManagerComponent,
    DialogModule,
    ButtonModule,
    MenuModule,
    CheckboxModule,
    InputTextModule,
    FloatLabelModule,
    ScrollPanelModule,
    ErrorMessageComponent,
  ],

  templateUrl: './tracking-list.component.html',
  styleUrl: './tracking-list.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [TreeTableFilterService, ViewDataService, ViewDialogService, ViewFilterService, TreeTableCoordinatorService],
})
export class TrackingListComponent implements OnInit, AfterViewInit, OnDestroy {
  // Signal queries
  private readonly treeTable = viewChild.required(MyArvatoTreeTableComponent);
  private readonly columnManagerComponent = viewChild.required(MyArvatoColumnManagerComponent);

  // Injections
  private readonly userColumnService = inject(UserColumnService);
  private readonly columnProviderService = inject(ColumnProviderService);
  private readonly detailNavigationService = inject(TransportDetailNavigationService);
  private readonly transportDataService = inject(TransportDataService);
  private readonly kpiService = inject(KpiService);
  private readonly trackingListExportService = inject(TrackingListExportService);
  private readonly translateService = inject(TranslateService);
  private readonly filterService = inject(TreeTableFilterService);
  private readonly router = inject(Router);
  private readonly injector = inject(Injector);
  private readonly dialogService = inject(MyArvatoDialogService);
  private readonly viewDataService = inject(ViewDataService);
  private readonly viewFilterService = inject(ViewFilterService);
  private readonly viewDialogService = inject(ViewDialogService);
  private readonly treeTableCoordinator = inject(TreeTableCoordinatorService);

  // Fields
  protected readonly matomoTrackerCategory: string = 'Tracking List';
  protected readonly errorMessage = signal<PrimeNGError | undefined>(undefined);
  protected readonly selectedDateRange = signal<DateRange | undefined>(undefined);
  protected readonly hasExternalFilters = signal<boolean>(false);
  private pendingExternalFilters: Record<string, unknown> | null = null;
  private readonly originalRouterStateFilters: Record<string, unknown> | null = null;
  protected readonly initialDataRange = signal<DateRange>({
    from: dayjs().startOf('day').subtract(90, 'days').toDate(),
    to: new Date(),
  });
  private hasTriggeredOnce = false;
  private hasAppliedStoredView = false;

  private readonly searchDialogRowTemplate = viewChild.required<
    TemplateRef<{
      $implicit: SearchDtoBase;
    }>
  >('searchDialogRowTemplate');
  protected readonly featureFlagTransportSearch = true;

  // Change detection
  protected readonly selectedColumns: WritableSignal<ColumnDefinitionBase[]> = signal([]);
  protected readonly availableColumns: WritableSignal<ColumnDefinitionBase[]> = signal([]);
  protected readonly treeLastFilter = signal<TreeTableLazyLoadEvent | undefined>(undefined);
  protected readonly kpiBarData = computed(() => this.kpiService.kpiData());
  protected readonly kpiBarLoading = computed(() => this.kpiService.isLoading());
  protected readonly kpiCardDefinitions = computed(() => this.kpiService.filteredCardDefinitions());
  protected readonly treeData = signal<OverviewRecordDto[]>([]);
  protected readonly treeTotalRecords = signal<number>(0);
  protected readonly resetTable = signal(false);
  protected readonly manualLazyLoad = signal<ManualLazyLoad | null>(null);
  protected readonly currentColumnOrder = computed(() => this.selectedColumns().map((col) => col.fieldsIdentifier));
  protected readonly selectedView = computed(() => this.viewDataService.selectedViewId());
  protected readonly isViewLoading = computed(() => {
    const viewsLoading = this.viewDataService.loadingStates().views;
    const programmaticUpdate = this.viewDialogService.isProgrammaticUpdate();
    const hasSelectedView = this.selectedView() != null;

    return viewsLoading || programmaticUpdate || (hasSelectedView && !this.hasAppliedStoredView);
  });

  protected defaultSelectedColumns: ColumnDefinitionBase[] = [];
  protected readonly defaultSortField = 'ShippingDate';
  protected readonly defaultSortOrder = -1;

  protected items: MenuItem[] | undefined;

  constructor() {
    const hasRouterStateFilters = history.state?.['list-filter'] != null;
    if (!hasRouterStateFilters) {
      this.selectedDateRange.set(this.initialDataRange());
    }

    const coordinationResult = this.treeTableCoordinator.coordinateTreeTable({
      dateFieldName: 'ShippingDate',
      dateRange: this.selectedDateRange,
      manualLazyLoad: this.manualLazyLoad,
      programmaticUpdate: this.viewDialogService.isProgrammaticUpdate,
      isViewLoading: this.isViewLoading,
      routerStateConfig: {
        sessionStorageKey: 'transport-tracking-list-router-state',
        routerStateKeys: {
          disableSelectedView: 'disable-selected-view',
          filters: 'list-filter',
        },
      },
    });

    if (coordinationResult.extractedState?.loaded && coordinationResult.extractedState?.filters) {
      const routerState = this.readRouterState();
      this.originalRouterStateFilters = routerState.listFilter || null;
      this.pendingExternalFilters = coordinationResult.extractedState.filters;

      // Clear router state after first use to prevent persistence on reload
      history.replaceState({}, '');
    }

    effect(() => {
      const saveRequested = this.viewDialogService.saveViewRequested();
      if (saveRequested) {
        this.handleSaveViewRequest();
        this.viewDialogService.resetSaveViewRequest();
      }
    });

    effect(() => {
      const viewId = this.viewDialogService.viewSelectionRequested();
      if (viewId) {
        this.handleViewSelectionRequest(viewId);
        this.viewDialogService.resetViewSelectionRequest();
      }
    });

    effect(() => {
      const viewId = this.viewDialogService.viewDeletionRequested();
      if (viewId) {
        this.handleViewDeletionRequest(viewId);
        this.viewDialogService.resetViewDeletionRequest();
      }
    });

    effect(() => {
      const selectedViewId = this.selectedView();
      const views = this.viewDataService.userViews();
      const standardViews = this.viewDataService.standardViews();
      const isViewsLoading = this.viewDataService.loadingStates().views;
      const selectedViewData = this.viewDataService.selectedView();

      if (
        !isViewsLoading &&
        selectedViewId &&
        selectedViewData &&
        !this.hasAppliedStoredView &&
        (views.length > 0 || standardViews.length > 0)
      ) {
        const allViews = [...standardViews, ...views];
        const viewExists = allViews.some((view) => view.id === selectedViewId);
        const needsApplication = this.shouldApplyStoredView();

        if (viewExists && needsApplication && !this.viewDialogService.isProgrammaticUpdate()) {
          this.hasAppliedStoredView = true;
          afterNextRender(
            () => {
              this.applyStoredView(selectedViewId);
            },
            { injector: this.injector },
          );
        }
      }
    });

    effect(() => {
      const isStandardView = this.viewDialogService.isStandardView();
      this.viewDialogService.setIsStandardViewChecked(isStandardView);
    });

    // Effect to clean up filters when columns are deselected
    effect(() => {
      const updatedSelection = this.selectedColumns();
      if (updatedSelection.length === 0) {
        return;
      }

      const treeTable = this.treeTable()?.treetable;
      if (!treeTable) {
        return;
      }

      const currentFilters = { ...treeTable.filters };
      const validFilterFields = new Set(
        updatedSelection.map((columnDefinitionBase) => columnDefinitionBase.filterInfo?.filterField).filter((field) => field !== undefined),
      );

      validFilterFields.add('ShippingDate');

      for (const kpiCard of this.kpiService.allCardDefinitions()) {
        if (kpiCard.filters) {
          Object.keys(kpiCard.filters).forEach((field) => validFilterFields.add(field));
        }
      }

      let hasRemovedFilters = false;
      for (const field in currentFilters) {
        if (!validFilterFields.has(field)) {
          delete currentFilters[field];
          hasRemovedFilters = true;
        }
      }

      if (hasRemovedFilters) {
        treeTable.filters = currentFilters;

        if ('filterConstraints' in treeTable) {
          const filterConstraints = (
            treeTable as {
              filterConstraints?: FilterRecord;
            }
          ).filterConstraints;
          if (filterConstraints) {
            for (const field in filterConstraints) {
              if (!validFilterFields.has(field)) {
                delete filterConstraints[field];
              }
            }
          }
        }

        if ('_filters' in treeTable) {
          const _filters = (treeTable as { _filters?: FilterRecord })._filters;
          if (_filters) {
            for (const field in _filters) {
              if (!validFilterFields.has(field)) {
                delete _filters[field];
              }
            }
          }
        }

        if (treeTable.columns) {
          treeTable.columns.forEach(
            (column: {
              field?: string;
              filterField?: string;
              filterValue?: string | null;
              filterMatchMode?: string | null;
              filter?: object | null;
              filterConstraint?: object | null;
              filterElement?: object | null;
              filterCallback?: object | null;
            }) => {
              const colField = column.field || column.filterField;
              if (colField && !validFilterFields.has(colField)) {
                if (column.filterValue !== undefined) {
                  column.filterValue = null;
                }
                if (column.filterMatchMode !== undefined) {
                  column.filterMatchMode = null;
                }
                if (column.filter) {
                  column.filter = null;
                }
                if (column.filterConstraint) {
                  column.filterConstraint = null;
                }
                if (column.filterElement) {
                  column.filterElement = null;
                }
                if (column.filterCallback) {
                  column.filterCallback = null;
                }
              }
            },
          );
        }

        const currentLastFilter = this.treeLastFilter();
        if (currentLastFilter) {
          this.treeLastFilter.set({
            ...currentLastFilter,
            filters: currentFilters,
          });
        }

        afterNextRender(
          () => {
            this.treeTable().resetTableTrigger.set(true);
          },
          { injector: this.injector },
        );
      }
    });
  }

  ngOnInit(): void {
    const availableColumns = this.columnProviderService.getColumns();
    this.availableColumns.set(availableColumns.filter((column) => column.visible()));

    this.userColumnService.getUserColumns().subscribe((columns) => {
      this.selectedColumns.set(columns);
      this.defaultSelectedColumns = [...columns];
    });

    this.items = [
      {
        label: this.translateService.instant('pages.transport.tracking-list.download.current-page'),
        items: [
          {
            label: this.translateService.instant('pages.transport.tracking-list.download.csv'),
            command: () => this.downloadCurrentPage('csv'),
          },
          {
            label: this.translateService.instant('pages.transport.tracking-list.download.xlsx'),
            command: () => this.downloadCurrentPage('xlsx'),
          },
        ],
      },
      {
        label: this.translateService.instant('pages.transport.tracking-list.download.all-pages'),
        items: [
          {
            label: this.translateService.instant('pages.transport.tracking-list.download.csv'),
            command: () => this.downloadAllPages('csv'),
          },
          {
            label: this.translateService.instant('pages.transport.tracking-list.download.xlsx'),
            command: () => this.downloadAllPages('xlsx'),
          },
        ],
      },
    ];
  }

  ngAfterViewInit(): void {
    if (this.pendingExternalFilters) {
      this.handleExternalFilters(this.pendingExternalFilters);
      this.pendingExternalFilters = null;
    }
  }

  ngOnDestroy(): void {
    this.hasTriggeredOnce = false;
  }

  private handleSaveViewRequest(): void {
    const viewName = this.viewDialogService.newViewName().trim();
    if (!viewName) {
      return;
    }

    const kpiConfig = {
      visibleKpiFields: this.kpiService.getVisibleKpiFields(),
      kpiOrder: this.kpiService.getKpiOrder(),
    };

    const currentViewState = this.viewFilterService.extractCurrentViewState(
      this.treeLastFilter(),
      this.currentColumnOrder(),
      this.selectedColumns(),
      kpiConfig,
    );

    const conflictInfo = this.viewDialogService.getConflictInfo();
    const dialogMode = conflictInfo.dialogMode;

    switch (dialogMode.mode) {
      case 'update': {
        const selectedViewId = this.selectedView();
        if (selectedViewId) {
          this.viewDataService.updateView(
            selectedViewId,
            viewName,
            currentViewState.normalizedFilters,
            currentViewState.sortField,
            currentViewState.sortOrder,
            currentViewState.rows,
            currentViewState.currentColumns,
            currentViewState.kpis,
          );
        }
        break;
      }

      case 'overwrite': {
        if (conflictInfo.conflictingView) {
          this.viewDataService.updateView(
            conflictInfo.conflictingView.id,
            viewName,
            currentViewState.normalizedFilters,
            currentViewState.sortField,
            currentViewState.sortOrder,
            currentViewState.rows,
            currentViewState.currentColumns,
            currentViewState.kpis,
          );
        }
        break;
      }

      case 'create':
      default: {
        this.viewDataService.saveView(
          viewName,
          currentViewState.normalizedFilters,
          currentViewState.sortField,
          currentViewState.sortOrder,
          currentViewState.rows,
          currentViewState.currentColumns,
          dialogMode.isStandardView,
          currentViewState.kpis,
        );
        break;
      }
    }

    this.viewDialogService.closeSaveViewDialog();
    this.viewDialogService.reset();
  }

  private handleViewSelectionRequest(viewId: string): void {
    this.selectView(viewId);
  }

  private handleViewDeletionRequest(viewId: string): void {
    if (this.selectedView() === viewId) {
      this.viewDataService.resetSelection();
    }
    this.viewDataService.deleteView(viewId);
  }

  protected openSaveViewDialog(): void {
    const selectedViewId = this.selectedView();
    const selectedView = this.viewDataService.selectedView();

    if (selectedViewId) {
      if (selectedView) {
        this.viewDialogService.setDialogData(selectedView.name, selectedView.name);
      } else {
        this.viewDialogService.setDialogData('', '');
      }
    } else {
      this.viewDialogService.setDialogData('', '');
    }
    this.viewDialogService.openSaveViewDialog();
  }

  protected onTableLazyLoaded(event: TreeTableLazyLoadEvent): void {
    if (this.viewDialogService.isProgrammaticUpdate()) {
      return;
    }

    if (!this.hasTriggeredOnce) {
      this.hasTriggeredOnce = true;
    }

    this.treeLastFilter.set(event);

    const effectiveDateRange = this.getEffectiveDateRange();

    if (this.hasExternalFilters() && this.originalRouterStateFilters) {
      const standardFilters = this.filterService.buildFiltersFromTreeEvent(event, effectiveDateRange);

      const mergedFilters = { ...standardFilters };

      Object.entries(this.originalRouterStateFilters).forEach(([key, value]) => {
        if (value !== null && value !== undefined) {
          mergedFilters[key] = value as FilterMetadata[];
        }
      });

      const dto: DataTableSearchDto = {
        first: event.first ?? undefined,
        rows: event.rows ?? undefined,
        sortField: typeof event.sortField === 'string' ? event.sortField : this.defaultSortField,
        sortOrder: typeof event.sortOrder === 'number' && event.sortOrder !== 0 ? event.sortOrder : this.defaultSortOrder,
        filters: mergedFilters,
        globalFilter: undefined,
        multiSortMeta: null,
        anchor: undefined,
      };

      this.loadOverviewAndKpis(dto, event, mergedFilters);
      this.hasExternalFilters.set(false);
    } else {
      const filters = this.filterService.buildFiltersFromTreeEvent(event, effectiveDateRange);

      const dto: DataTableSearchDto = {
        first: event.first ?? undefined,
        rows: event.rows ?? undefined,
        sortField: typeof event.sortField === 'string' ? event.sortField : this.defaultSortField,
        sortOrder: typeof event.sortOrder === 'number' && event.sortOrder !== 0 ? event.sortOrder : this.defaultSortOrder,
        filters: filters,
        globalFilter: undefined,
        multiSortMeta: null,
        anchor: undefined,
      };

      this.loadOverviewAndKpis(dto, event, filters);
    }
  }

  protected onKpiCardClicked(kpiEvent: KpiCardDefinition): void {
    const treeTableFilters = this.treeLastFilter()?.filters ?? {};
    const kpiClickResult = this.kpiService.handleKpiCardClick(kpiEvent, treeTableFilters as FilterRecord);

    this.viewDialogService.isProgrammaticUpdate.set(true);

    if (kpiClickResult.shouldRemoveFilters) {
      this.selectedColumns.update((currentColumns) => {
        const baseColumnList = [...currentColumns];
        const columnsToRemove = kpiClickResult.filterFieldsToRemove;

        //only necessary for the dynamically added/removed column "Days Delayed"
        return baseColumnList.filter((column) => {
          const filterField = column.filterInfo?.filterField;
          if (!filterField || !columnsToRemove.includes(filterField)) {
            return true;
          }

          return this.defaultSelectedColumns.some((defaultColumn) => defaultColumn.filterInfo?.filterField === filterField);
        });
      });

      this.applyKpiFiltersAndReload(kpiClickResult.filters);
      this.viewDialogService.isProgrammaticUpdate.set(false);
      return;
    }

    this.selectedColumns.update((currentColumns) => {
      const baseColumnList = [...currentColumns];
      kpiClickResult.columnsToAdd.forEach((field) => {
        const columnExists = baseColumnList.some((c) => c.filterInfo?.filterField === field);
        if (!columnExists) {
          const newColumn = this.availableColumns().find((c) => c.filterInfo?.filterField === field);
          if (newColumn) {
            baseColumnList.push(newColumn);
          }
        }
      });
      return baseColumnList;
    });

    this.applyKpiFiltersAndReload(kpiClickResult.filters);
    this.viewDialogService.isProgrammaticUpdate.set(false);
  }

  private applyKpiFiltersAndReload(kpiFilters: FilterRecord): void {
    const currentDateRange = this.selectedDateRange() ?? this.initialDataRange();
    const currentDateRangeFilter = this.filterService.createDateRangeFilter(currentDateRange, 'ShippingDate');
    const mergedFilters = this.filterService.combineFilters(kpiFilters, currentDateRangeFilter, {});

    this.treeTable().setExternalFilters(mergedFilters);

    const currentTreeTable = this.treeTable().treetable;
    const currentLazyLoadEvent: TreeTableLazyLoadEvent = {
      first: currentTreeTable.first || 0,
      last: (currentTreeTable.first || 0) + (currentTreeTable.rows || 10) - 1,
      rows: currentTreeTable.rows || 10,
      filters: currentTreeTable.filters,
      sortField: currentTreeTable.sortField,
      sortOrder: currentTreeTable.sortOrder,
    };

    const currentDto: DataTableSearchDto = {
      first: currentLazyLoadEvent.first ?? undefined,
      rows: currentLazyLoadEvent.rows ?? undefined,
      sortField: typeof currentLazyLoadEvent.sortField === 'string' ? currentLazyLoadEvent.sortField : this.defaultSortField,
      sortOrder:
        typeof currentLazyLoadEvent.sortOrder === 'number' && currentLazyLoadEvent.sortOrder !== 0
          ? currentLazyLoadEvent.sortOrder
          : this.defaultSortOrder,
      filters: this.filterService.buildFiltersFromTreeEvent(currentLazyLoadEvent, this.getEffectiveDateRange()),
      globalFilter: undefined,
      multiSortMeta: null,
      anchor: undefined,
    };

    this.loadOverviewAndKpis(currentDto, currentLazyLoadEvent, currentDto.filters || {});
    this.treeLastFilter.set(currentLazyLoadEvent);
  }

  private loadOverviewAndKpis(dto: DataTableSearchDto, event: TreeTableLazyLoadEvent, filters: FilterRecord): void {
    this.transportDataService.getTreeOverview(dto).subscribe({
      next: (overview: OverviewDto) => {
        this.treeTotalRecords.set(overview.count);
        this.treeData.set(overview.records);
      },
      error: () => {
        this.treeTotalRecords.set(0);
        this.treeData.set([]);

        const message: PrimeNGError = {
          summary: this.translateService.instant('pages.transport.errors.general.summary'),
          detail: this.translateService.instant('pages.transport.errors.general.detail'),
        };

        this.errorMessage.set(message);
      },
    });

    if ((event.first ?? 0) === 0) {
      this.kpiService.loadKpis({
        first: typeof event.first === 'number' ? event.first : undefined,
        rows: typeof event.rows === 'number' ? event.rows : undefined,
        sortField: typeof event.sortField === 'string' ? event.sortField : this.defaultSortField,
        sortOrder: typeof event.sortOrder === 'number' && event.sortOrder !== 0 ? event.sortOrder : this.defaultSortOrder,
        filters: (() => {
          return filters;
        })(),
        globalFilter: null,
        multiSortMeta: null,
      });
    }
  }

  protected onTableColumnReordered(event: TreeTableColumnReorderEvent): void {
    const reorderedColumns = [...this.selectedColumns()];

    if (event.dragIndex !== undefined && event.dropIndex !== undefined) {
      const dragIndex = event.dragIndex;
      const dropIndex = event.dropIndex;
      const [movedColumn] = reorderedColumns.splice(dragIndex, 1);
      reorderedColumns.splice(dropIndex, 0, movedColumn);

      this.selectedColumns.set(reorderedColumns);
    }
  }

  protected async onRowSelected(node: TreeNode<OverviewRecordDto> | null) {
    if (node?.data == null || node.data.ehGuid == null || node.data.trackingNumber == null) {
      return;
    }

    await this.detailNavigationService.openViaTrackingNumber(node.data.trackingNumber, node.data.ehGuid);
  }

  protected onResetTable(): void {
    this.viewDialogService.isProgrammaticUpdate.set(true);
    this.hasAppliedStoredView = false;
    this.hasExternalFilters.set(false);

    const initialDateRange = this.initialDataRange();
    this.selectedDateRange.set(initialDateRange);

    this.viewDataService.resetSelection();

    this.selectedColumns.set([...this.defaultSelectedColumns]);

    this.kpiService.resetToDefaultKpis();

    this.viewDialogService.requestDropdownClose();

    const dateRangeFilter = this.filterService.createDateRangeFilter(initialDateRange, 'ShippingDate');
    const filters: FilterRecord = {};
    for (const field in dateRangeFilter) {
      const filterArray = dateRangeFilter[field];
      if (filterArray && filterArray.length > 0) {
        filters[field] = filterArray;
      }
    }

    const rows = this.treeTable().pageRowsPerPage();

    this.treeLastFilter.set({
      first: 0,
      rows: rows,
      filters: { ...filters },
      sortField: this.defaultSortField,
      sortOrder: this.defaultSortOrder,
    } as TreeTableLazyLoadEvent);

    const dto: DataTableSearchDto = {
      first: 0,
      rows: rows,
      sortField: this.defaultSortField,
      sortOrder: this.defaultSortOrder,
      filters: this.filterService.buildFiltersFromTreeEvent(
        {
          first: 0,
          rows: rows,
          filters: filters,
          sortField: this.defaultSortField,
          sortOrder: this.defaultSortOrder,
        } as TreeTableLazyLoadEvent,
        this.getEffectiveDateRange(),
      ),
      globalFilter: undefined,
      multiSortMeta: null,
      anchor: undefined,
    };

    this.loadOverviewAndKpis(
      dto,
      {
        first: 0,
        rows: rows,
        filters: filters,
        sortField: this.defaultSortField,
        sortOrder: this.defaultSortOrder,
      } as TreeTableLazyLoadEvent,
      dto.filters || {},
    );

    this.treeTable().filtersAfterReset.set({ ...filters });
    this.treeTable().resetTableTrigger.set(true);

    afterNextRender(
      () => {
        const columnManager = this.columnManagerComponent();
        columnManager.resetSelection();

        this.viewDialogService.isProgrammaticUpdate.set(false);
      },
      { injector: this.injector },
    );
  }

  protected async downloadCurrentPage(type: 'csv' | 'xlsx'): Promise<void> {
    await this.trackingListExportService.exportCurrentPage(this.treeData(), this.selectedColumns(), type);
  }

  protected async downloadAllPages(type: 'csv' | 'xlsx'): Promise<void> {
    await this.trackingListExportService.exportAllPages(this.treeLastFilter(), this.selectedColumns(), this.treeTotalRecords(), type);
  }

  protected async onSearchbarClick(): Promise<void> {
    const dialogRef = this.dialogService.showDialog(SearchWidgetComponent, {
      ...getSearchWidgetConfig(TransportSearchService),
      inputValues: {
        maxSearchLength: TRANSPORT_SEARCH_ALLOWED_MAX_LENGTH,
        rowTemplate: this.searchDialogRowTemplate(),
        trackerCategory: 'Transport - List - Search',
        translationKey: 'pages.transport.search-widget',
      },
    });

    const result = await getDialogResult<TransportSearchIndexDto>(dialogRef);
    if (!result || !result?.ehGuid || !result.trackingNumber) {
      return;
    }

    // For now, we directly jump to the tabs instead of showing the details dialog
    const newTab: UserTabDto<TransportUserTabDataDto> = {
      displayName: result.trackingNumber,
      context: 'transport-tabs-view',
      data: { transportId: result.ehGuid },
    };

    this.router
      .navigate(['transport', 'tabs'], {
        state: newTab,
      })
      .then();
  }

  protected selectView(viewId: string): void {
    this.viewDialogService.isProgrammaticUpdate.set(true);
    this.hasAppliedStoredView = true;

    this.viewDataService.selectView(viewId);
    const view = this.viewDataService.getUserView(viewId);

    if (!(view && view.value.filters)) {
      this.viewDialogService.isProgrammaticUpdate.set(false);
      return;
    }

    const applyResult: ViewApplicationResult = this.viewFilterService.applyViewToTable(
      view.value,
      this.treeTable(),
      this.initialDataRange(),
      this.columnProviderService,
    );

    if (applyResult.dateRange) {
      this.selectedDateRange.set(applyResult.dateRange);
    }

    if (applyResult.columns && applyResult.columns.length > 0) {
      this.selectedColumns.set(applyResult.columns);
    }

    this.restoreKpiStateFromView(view.value);

    this.loadOverviewAndKpis(applyResult.searchDto, applyResult.lazyLoadEvent, applyResult.searchDto.filters || {});
    this.treeLastFilter.set(applyResult.lazyLoadEvent);

    this.viewDialogService.requestDropdownClose();

    afterNextRender(
      () => {
        this.viewDialogService.isProgrammaticUpdate.set(false);
      },
      { injector: this.injector },
    );
  }

  private restoreKpiStateFromView(viewValue: { kpis?: KpiStateDto }): void {
    const kpis = viewValue.kpis;

    if (!kpis) {
      this.kpiService.resetToDefaultKpis();
      return;
    }

    const defaultOrder = this.kpiService.allCardDefinitions().map((definition) => definition.field);

    const visibleFields = Array.isArray(kpis.visibleKpiFields) ? kpis.visibleKpiFields : this.kpiService.cardDefinitionDefaults();
    const order = Array.isArray(kpis.kpiOrder) ? kpis.kpiOrder : defaultOrder;

    this.kpiService.setVisibleKpiFields(visibleFields);
    this.kpiService.setKpiOrder(order);
  }

  private applyStoredView(viewId: string): void {
    if (this.viewDialogService.isProgrammaticUpdate()) {
      return;
    }

    const view = this.viewDataService.getUserView(viewId);
    if (!view || !view.value.filters) {
      return;
    }

    // Reset external filters flag when applying stored view
    this.hasExternalFilters.set(false);

    this.viewDialogService.isProgrammaticUpdate.set(true);

    const applyResult = this.viewFilterService.applyViewToTable(
      view.value,
      this.treeTable(),
      this.initialDataRange(),
      this.columnProviderService,
    );

    if (applyResult.dateRange) {
      this.selectedDateRange.set(applyResult.dateRange);
    }

    if (applyResult.columns && applyResult.columns.length > 0) {
      this.selectedColumns.set(applyResult.columns);
    }

    this.restoreKpiStateFromView(view.value);

    this.treeLastFilter.set(applyResult.lazyLoadEvent);

    this.loadOverviewAndKpis(applyResult.searchDto, applyResult.lazyLoadEvent, applyResult.searchDto.filters || {});

    afterNextRender(
      () => {
        this.viewDialogService.isProgrammaticUpdate.set(false);
      },
      { injector: this.injector },
    );
  }

  private getEffectiveDateRange(): DateRange | undefined {
    const hasExternal = this.hasExternalFilters();
    const selectedRange = this.selectedDateRange();
    const initialRange = this.initialDataRange();

    if (hasExternal) {
      return undefined;
    } else {
      return selectedRange ?? initialRange;
    }
  }

  private shouldApplyStoredView(): boolean {
    const currentFilter = this.treeLastFilter();
    return !currentFilter || !currentFilter.filters || Object.keys(currentFilter.filters).length <= 1;
  }

  private handleExternalFilters(filters: Record<string, unknown>): void {
    const filterRecord: FilterRecord = {};
    let hasExternalShippingDateFilter = false;
    let extractedDateRange: DateRange | undefined = undefined;

    Object.entries(filters).forEach(([key, value]) => {
      if (value !== null && value !== undefined) {
        if (key === 'ShippingDate' && Array.isArray(value) && value.length > 0) {
          hasExternalShippingDateFilter = true;

          const gteFilter = value.find((f: FilterMetadata) => f.matchMode === 'gte');
          const lteFilter = value.find((f: FilterMetadata) => f.matchMode === 'lte');

          if (gteFilter && lteFilter) {
            extractedDateRange = {
              from: new Date(gteFilter.value),
              to: new Date(lteFilter.value),
            };
          }

          filterRecord[key] = value as FilterMetadata[];
        } else {
          filterRecord[key] = value as FilterMetadata[];
        }
      }
    });

    this.addRequiredColumnsForExternalFilters(filterRecord);

    if (hasExternalShippingDateFilter) {
      this.hasExternalFilters.set(true);
      this.selectedDateRange.set(extractedDateRange);
    } else {
      this.hasExternalFilters.set(true);
    }

    this.manualLazyLoad.set({
      filters: filterRecord,
      clearOthers: false,
      resetSorting: false,
      resetPagination: true,
    });

    // Set filter UI state after the load is triggered
    Promise.resolve().then(() => {
      this.setFilterUIState(filterRecord);
    });
  }

  private addRequiredColumnsForExternalFilters(filterRecord: FilterRecord): void {
    const columnsToAdd: string[] = [];

    if (filterRecord['DaysDelayed']) {
      columnsToAdd.push('DaysDelayed');
    }

    if (columnsToAdd.length > 0) {
      this.selectedColumns.update((currentColumns) => {
        const baseColumnList = [...currentColumns];

        columnsToAdd.forEach((fieldName) => {
          const columnExists = baseColumnList.some((c) => c.filterInfo?.filterField === fieldName);
          if (!columnExists) {
            const newColumn = this.availableColumns().find((c) => c.filterInfo?.filterField === fieldName);
            if (newColumn) {
              baseColumnList.push(newColumn);
            }
          }
        });

        return baseColumnList;
      });
    }
  }

  private readRouterState(): { listFilter?: Record<string, unknown>; disableSelectedView?: boolean } {
    const navigation = this.router.getCurrentNavigation();
    const state = navigation?.extras?.state;

    return {
      listFilter: state?.['list-filter'],
      disableSelectedView: state?.['disable-selected-view'] === true,
    };
  }

  private setFilterUIState(filterRecord: FilterRecord): void {
    // Manually set UI filter states without triggering HTTP requests
    // This is needed because manualLazyLoad doesn't update the filter component UI
    const treeTableComponent = this.treeTable();

    if (treeTableComponent && 'setExternalFilters' in treeTableComponent) {
      treeTableComponent.setExternalFilters(filterRecord);
    }
  }
}
